import { useState, useEffect } from "react";
import type { EnrichedLead } from "@/lib/data";
import { SearchBar } from "@/components/SearchBar";
import { LeadCard } from "@/components/LeadCard";
import { LeadDetail } from "@/components/LeadDetail";
import { Card, CardContent } from "@/components/ui/card";
import {
	Building2,
	Users,
	Zap,
	TrendingUp,
	BarChart3,
	Filter,
	ArrowUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type SortField = "score" | "name" | "signals" | "revenue";
type FilterIndustry = string | null;

export function Dashboard() {
	const [leads, setLeads] = useState<EnrichedLead[]>([]);
	const [filteredLeads, setFilteredLeads] = useState<EnrichedLead[]>([]);
	const [selectedLead, setSelectedLead] = useState<EnrichedLead | null>(null);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortField, setSortField] = useState<SortField>("score");
	const [sortAsc, setSortAsc] = useState(false);
	const [filterIndustry, setFilterIndustry] = useState<FilterIndustry>(null);

	useEffect(() => {
		fetchLeads("");
	}, []);

	async function fetchLeads(query: string) {
		setLoading(true);
		try {
			const res = await fetch(`/api/leads?q=${encodeURIComponent(query)}`);
			const data = await res.json();
			setLeads(data.leads);
			setFilteredLeads(data.leads);
		} catch {
			setLeads([]);
			setFilteredLeads([]);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		let result = [...leads];

		if (filterIndustry) {
			result = result.filter((l) => l.company.industry === filterIndustry);
		}

		result.sort((a, b) => {
			let cmp = 0;
			switch (sortField) {
				case "score": cmp = a.score.overall - b.score.overall; break;
				case "name": cmp = a.company.name.localeCompare(b.company.name); break;
				case "signals": cmp = a.signals.length - b.signals.length; break;
				case "revenue": {
					const rA = parseInt(a.company.revenue.replace(/[^0-9]/g, ""));
					const rB = parseInt(b.company.revenue.replace(/[^0-9]/g, ""));
					cmp = rA - rB;
					break;
				}
			}
			return sortAsc ? cmp : -cmp;
		});

		setFilteredLeads(result);
	}, [leads, sortField, sortAsc, filterIndustry]);

	function handleSearch(query: string) {
		setSearchQuery(query);
		setFilterIndustry(null);
		fetchLeads(query);
	}

	function handleSelect(id: string, type: string) {
		if (type === "industry") {
			setFilterIndustry(id);
			fetchLeads("");
		} else {
			const lead = leads.find((l) => l.company.id === id);
			if (lead) setSelectedLead(lead);
			else {
				fetch(`/api/leads/${id}`)
					.then((r) => r.json())
					.then((data) => {
						if (!data.error) setSelectedLead(data);
					});
			}
		}
	}

	function toggleSort(field: SortField) {
		if (sortField === field) setSortAsc(!sortAsc);
		else { setSortField(field); setSortAsc(false); }
	}

	const industries = [...new Set(leads.map((l) => l.company.industry))];
	const hotLeads = leads.filter((l) => l.score.overall >= 80).length;
	const warmLeads = leads.filter((l) => l.score.overall >= 60 && l.score.overall < 80).length;
	const totalSignals = leads.reduce((acc, l) => acc + l.signals.length, 0);
	const totalContacts = leads.reduce((acc, l) => acc + l.contacts.length, 0);

	if (selectedLead) {
		return (
			<div className="max-w-5xl mx-auto">
				<LeadDetail lead={selectedLead} onBack={() => setSelectedLead(null)} />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Hero / Search Section */}
			<div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-8 text-white">
				<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
				<div className="relative z-10">
					<div className="flex items-center gap-3 mb-2">
						<div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
							<BarChart3 className="w-5 h-5" />
						</div>
						<div>
							<h1 className="text-2xl font-bold">LeadLens</h1>
							<p className="text-blue-200 text-sm">AI-Powered Data Enrichment & Lead Intelligence</p>
						</div>
					</div>
					<div className="mt-6">
						<SearchBar onSearch={handleSearch} onSelect={handleSelect} />
					</div>
				</div>
			</div>

			{/* Stats Bar */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
				<Card className="bg-white border-slate-200">
					<CardContent className="p-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
							<Building2 className="w-5 h-5 text-blue-600" />
						</div>
						<div>
							<div className="text-2xl font-bold text-foreground">{leads.length}</div>
							<div className="text-xs text-muted-foreground">Companies</div>
						</div>
					</CardContent>
				</Card>
				<Card className="bg-white border-slate-200">
					<CardContent className="p-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
							<TrendingUp className="w-5 h-5 text-emerald-600" />
						</div>
						<div>
							<div className="text-2xl font-bold text-foreground">{hotLeads}</div>
							<div className="text-xs text-muted-foreground">Hot Leads</div>
						</div>
					</CardContent>
				</Card>
				<Card className="bg-white border-slate-200">
					<CardContent className="p-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
							<Zap className="w-5 h-5 text-violet-600" />
						</div>
						<div>
							<div className="text-2xl font-bold text-foreground">{totalSignals}</div>
							<div className="text-xs text-muted-foreground">Intent Signals</div>
						</div>
					</CardContent>
				</Card>
				<Card className="bg-white border-slate-200">
					<CardContent className="p-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
							<Users className="w-5 h-5 text-amber-600" />
						</div>
						<div>
							<div className="text-2xl font-bold text-foreground">{totalContacts}</div>
							<div className="text-xs text-muted-foreground">Contacts</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Filters and Sort */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-wrap items-center gap-2">
					<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
						<Filter className="w-4 h-4" />
						<span>Filter:</span>
					</div>
					<Button
						variant={filterIndustry === null ? "default" : "outline"}
						size="sm"
						className={`text-xs h-7 ${filterIndustry === null ? "bg-blue-600 hover:bg-blue-700" : ""}`}
						onClick={() => setFilterIndustry(null)}
					>
						All
					</Button>
					{industries.map((ind) => (
						<Button
							key={ind}
							variant={filterIndustry === ind ? "default" : "outline"}
							size="sm"
							className={`text-xs h-7 ${filterIndustry === ind ? "bg-blue-600 hover:bg-blue-700" : ""}`}
							onClick={() => setFilterIndustry(filterIndustry === ind ? null : ind)}
						>
							{ind}
						</Button>
					))}
				</div>
				<div className="flex items-center gap-2">
					<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
						<ArrowUpDown className="w-4 h-4" />
						<span>Sort:</span>
					</div>
					{(["score", "name", "signals", "revenue"] as SortField[]).map((field) => (
						<Button
							key={field}
							variant={sortField === field ? "default" : "outline"}
							size="sm"
							className={`text-xs h-7 capitalize ${sortField === field ? "bg-blue-600 hover:bg-blue-700" : ""}`}
							onClick={() => toggleSort(field)}
						>
							{field}
							{sortField === field && (
								<span className="ml-1">{sortAsc ? "\u2191" : "\u2193"}</span>
							)}
						</Button>
					))}
				</div>
			</div>

			{/* Results count */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{filteredLeads.length} result{filteredLeads.length !== 1 ? "s" : ""}
					{searchQuery && <span> for "<span className="font-medium text-foreground">{searchQuery}</span>"</span>}
					{filterIndustry && <span> in <Badge variant="secondary" className="ml-1 text-xs">{filterIndustry}</Badge></span>}
				</p>
			</div>

			{/* Lead Cards */}
			{loading ? (
				<div className="space-y-3">
					{[1, 2, 3].map((i) => (
						<Card key={i} className="bg-white border-slate-200 animate-pulse">
							<CardContent className="p-5">
								<div className="flex items-start gap-4">
									<div className="w-12 h-12 rounded-lg bg-slate-200" />
									<div className="flex-1 space-y-3">
										<div className="h-4 bg-slate-200 rounded w-1/3" />
										<div className="h-3 bg-slate-200 rounded w-1/4" />
										<div className="h-3 bg-slate-200 rounded w-2/3" />
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			) : filteredLeads.length === 0 ? (
				<Card className="bg-white border-slate-200">
					<CardContent className="p-12 text-center">
						<Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
						<h3 className="text-lg font-semibold text-foreground">No results found</h3>
						<p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-3">
					{filteredLeads.map((lead) => (
						<LeadCard
							key={lead.company.id}
							lead={lead}
							onClick={() => setSelectedLead(lead)}
						/>
					))}
				</div>
			)}
		</div>
	);
}
